import { ObjectSchema } from "@hapi/joi";

import Joi, {
  getNumericErrors,
  getAttachmentsSchema,
  scaleTicketSchema,
} from "fe-shared/src/helpers/validation";
import { ErrorMessages, FileInterface } from "fe-shared/src/types/common";
import {
  COMMENT_LENGTH_LIMIT,
  adjustmentReasonOtherCode,
} from "../../../consts";
import {
  CorrectionsFormContent,
  ScaleTicketCorrectionsForm,
  CorrectionMaterial,
} from "../../../types";

export const MAX_TIRES_COUNT = 99999;
export const MAX_SINGLE_WEIGHT = 999999999999;
export const MAX_TOTAL_WEIGHT = 99999999999999;

export const getInitialContent = (
  isAdjustment: boolean,
  attachments: FileInterface[],
  isWithWeightAndScale: boolean,
  comment: string
): CorrectionsFormContent => ({
  materials: [],
  materialAttributes: [],
  comment: isAdjustment ? comment : "",
  attachments,
  ...(isWithWeightAndScale
    ? {
        scaleTickets: [],
        grossWeight: 0,
        tareWeight: 0,
      }
    : {}),
  ...(isAdjustment
    ? {
        adjustmentReasons: [],
        adjustmentOtherReasonComment: "",
      }
    : {}),
});

export const errorMessages: ErrorMessages = {
  adjustmentReasons: {
    "array.min": "validation.adjustmentReasonsRequired",
  },
  adjustmentOtherReasonComment: {
    "string.empty": "validation.adjustmentOtherCommentRequired",
    "string.max": "validation.adjustmentOtherCommentIsTooLong",
  },
  materials: {
    "array.min": "validation.materialsRequired",
  },
  unitQuantity: {
    ...getNumericErrors(
      "validation.quantityRequired",
      "validation.quantityInvalid"
    ),
  },
  scaleTickets: {
    "array.min": "validation.ticketNumberRequired",
  },
  "scaleTickets.initialTicketNumber": {
    "string.empty": "validation.ticketNumberRequired",
    "string.pattern.name": "validation.ticketNumberInvalid",
    "string.max": "validation.ticketNumberRequired",
  },
  "scaleTickets.correctedTicketNumber": {
    "string.empty": "validation.ticketNumberRequired",
    "string.pattern.name": "validation.ticketNumberInvalid",
    "string.max": "validation.ticketNumberRequired",
  },
  grossWeight: {
    ...getNumericErrors("validation.grossRequired", "validation.grossInvalid"),
  },
  tareWeight: {
    ...getNumericErrors("validation.tareRequired", "validation.tareInvalid"),
  },
};

const tiresCountSchema: ObjectSchema<CorrectionMaterial> = Joi.object({
  materialCode: Joi.string(),
  units: Joi.object({
    unit: Joi.any(),
    unitQuantity: Joi.number().integer().min(1).max(MAX_TIRES_COUNT).required(),
  }),
}).unknown();

const materialsCountSchema: ObjectSchema<CorrectionMaterial> = Joi.object({
  materialCode: Joi.string(),
  units: Joi.object({
    unit: Joi.any(),
    unitQuantity: Joi.number()
      .integer()
      .min(1)
      .max(MAX_SINGLE_WEIGHT)
      .required(),
  }),
}).unknown();

const scaleTicketsSchema: ObjectSchema<ScaleTicketCorrectionsForm> = Joi.object(
  {
    initialTicketNumber: Joi.any(),
    ticketId: Joi.number().integer().positive().allow(null),
    correctedTicketNumber: scaleTicketSchema.required(),
  }
);

const getBasicValidations = (isTransactionWithTires: boolean) => ({
  materials: Joi.array()
    .items(isTransactionWithTires ? tiresCountSchema : materialsCountSchema)
    .min(1),
  materialAttributes: Joi.array().items(Joi.string()),
  comment: Joi.string().max(COMMENT_LENGTH_LIMIT).allow("", null),
  attachments: getAttachmentsSchema("attachments"),
});

const weightAndScaleValidations = {
  scaleTickets: Joi.array().items(scaleTicketsSchema).min(1),
  grossWeight: Joi.number()
    .integer()
    .min(Joi.ref("tareWeight"))
    .max(MAX_TOTAL_WEIGHT)
    .required(),
  tareWeight: Joi.number().integer().min(1).max(MAX_TOTAL_WEIGHT).required(),
};

const adjustmentValidations = {
  adjustmentReasons: Joi.array().items(Joi.string()).min(1),
  adjustmentOtherReasonComment: Joi.optional().when("adjustmentReasons", {
    is: Joi.array().items(
      Joi.string(),
      Joi.valid(adjustmentReasonOtherCode).required()
    ),
    then: Joi.string().required(),
  }),
};

export const getValidationConfig = (
  isAdjustment: boolean,
  isWithWeightAndScale: boolean,
  isTransactionWithTires: boolean
): ObjectSchema<CorrectionsFormContent> =>
  Joi.object({
    ...getBasicValidations(isTransactionWithTires),
    ...(isAdjustment ? adjustmentValidations : {}),
    ...(isWithWeightAndScale ? weightAndScaleValidations : {}),
  });
